import { getSession } from '../auth';
import { toast } from '../components/toast';
import {
  isProductionData, getCourse, listCourseMaterials, createCourseMaterial,
  deleteCourseMaterial, type CourseMaterial,
} from '../lib/repository';
import { openModal, closeModal } from '../components/modal';

type Tab = 'videos' | 'quizzes' | 'notes';

let activeTab: Tab = 'videos';
let courseCache: Awaited<ReturnType<typeof getCourse>> | null = null;
let cachedCourseId = '';
let materialsCache: CourseMaterial[] | null = null;
let cachedMaterialsCourseId = '';
let loadingMaterials = false;
let courseLoading = false;

function youtubeId(url: string): string | null {
  // accept youtu.be/X, youtube.com/watch?v=X, youtube.com/embed/X, youtube.com/shorts/X
  const m = url.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([\w-]{6,})/);
  return m ? m[1] : null;
}

async function loadCourse(courseId: string): Promise<void> {
  if (cachedCourseId === courseId && courseCache) return;
  courseCache = null;
  cachedCourseId = courseId;
  courseLoading = true;
  try { courseCache = await getCourse(courseId); } catch { courseCache = null; }
  courseLoading = false;
  (window as any).__rerender?.();
}

async function loadMaterials(courseId: string): Promise<void> {
  if (cachedMaterialsCourseId === courseId && materialsCache) return;
  materialsCache = null;
  cachedMaterialsCourseId = courseId;
  loadingMaterials = true;
  try { materialsCache = await listCourseMaterials(courseId); } catch { materialsCache = []; }
  loadingMaterials = false;
  (window as any).__rerender?.();
}

function courseIdFromHash(): string {
  const m = location.hash.match(/course-detail\/([^/?]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

export function renderCourseDetail(courseId: string): string {
  void loadCourse(courseId);
  void loadMaterials(courseId);

  const session = getSession();
  const canManage = !!session && ['owner', 'admin', 'teamLead', 'educator'].includes(session.role);

  const c = courseCache;
  const materials = materialsCache || [];
  const videos = materials.filter((m) => m.type === 'video');
  const quizzes = materials.filter((m) => m.type === 'quiz' || m.type === 'kahoot');
  const notes = materials.filter((m) => m.type === 'note' || m.type === 'document' || m.type === 'ppt');

  if (courseLoading && !c) {
    return '<section class="hero stagger"><div><div class="hero-greet">Course</div><h1 class="hero-title">Loading…</h1></div></section>';
  }
  if (!c) {
    return '<section class="hero stagger"><div><div class="hero-greet">Course</div><h1 class="hero-title">Not found</h1></div></section>' +
      '<div class="card card-pad-lg"><div class="empty">No course with id <code>' + courseId + '</code>. <a href="#/courses" style="color:var(--accent)">Back to courses</a></div></div>';
  }

  const tabs: Array<[Tab, string, number]> = [['videos', '🎬 Videos', videos.length], ['quizzes', '📝 Quizzes', quizzes.length], ['notes', '📚 Notes', notes.length]];
  const tabBtns = tabs.map(([k, label, n]) => '<button class="touch-target ' + (activeTab === k ? 'active' : '') + '" data-cd-tab="' + k + '">' + label + ' <span class="t-meta">' + n + '</span></button>').join('');

  const panel = (() => {
    if (loadingMaterials && materials.length === 0) {
      return '<div class="attendance-empty"><h3>Loading…</h3><p>Fetching course content from the database.</p></div>';
    }
    if (activeTab === 'videos') return renderVideosPanel(videos, canManage);
    if (activeTab === 'quizzes') return renderQuizzesPanel(quizzes, canManage);
    return renderNotesPanel(notes, canManage);
  })();

  return '<section class="hero stagger"><div><div class="hero-greet">Course · ' + (c.code || '—') + '</div><h1 class="hero-title">' + (c.title || 'Untitled') + '</h1><div class="row" style="gap:8px;margin-top:8px"><span class="pill ' + (c.status === 'live' ? 'pill-success' : 'pill-info') + '">' + (c.status || 'draft') + '</span><span class="t-meta">' + (c.durationWeeks || 6) + ' weeks · ' + (c.level || 'Foundation') + '</span></div></div>' +
    '<div class="hero-aside"><a class="btn btn-ghost touch-target" href="#/courses">← All courses</a>' + (canManage ? '<button type="button" class="btn btn-accent touch-target" data-cd-action="add">+ Add to ' + (activeTab === 'videos' ? 'Videos' : activeTab === 'quizzes' ? 'Quizzes' : 'Notes') + '</button>' : '') + '</div></section>' +
    '<div class="tabs" style="margin-bottom:var(--s-4)">' + tabBtns + '</div>' +
    '<div class="stagger">' + panel + '</div>';
}

function renderVideosPanel(videos: CourseMaterial[], canManage: boolean): string {
  if (!videos.length) return '<div class="attendance-empty"><h3>No videos yet</h3><p>Videos are <strong>YouTube URLs only</strong> (private uploads from your channel — free unlimited cloud + analytics). The form accepts any YouTube link and plays it inside this page in a privacy-enhanced embed.</p>' + (canManage ? '<button type="button" class="btn btn-primary" data-cd-action="add">+ Add first video</button>' : '') + '</div>';
  return '<div class="video-grid stagger">' + videos.map((v) => {
    const id = youtubeId(v.url);
    const embed = id ? 'https://www.youtube-nocookie.com/embed/' + id : '';
    return '<div class="card card-pad-sm video-card" data-cd-mat="' + v.id + '">' +
      '<div class="row" style="margin-bottom:10px">' +
        '<div style="flex:1;min-width:0"><div class="t-strong">' + escapeHtml(v.title) + '</div>' +
        (v.note ? '<div class="t-meta">' + escapeHtml(v.note) + '</div>' : '') + '</div>' +
        (canManage ? '<button type="button" class="btn btn-ghost btn-sm" data-cd-delete="' + v.id + '" title="Delete">✕</button>' : '') +
      '</div>' +
      (embed ? '<div class="video-embed" style="position:relative;padding-top:56.25%;border-radius:10px;overflow:hidden;background:#000"><iframe src="' + embed + '" title="' + escapeHtml(v.title) + '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen style="position:absolute;top:0;left:0;width:100%;height:100%"></iframe></div>' : '<div class="t-meta" style="color:var(--warn)">Invalid YouTube URL — open this material to edit it.</div>') +
      '<div class="t-meta" style="margin-top:8px;font-size:11px"><a href="' + escapeHtml(v.url) + '" target="_blank" rel="noopener" style="color:var(--accent)">Watch on YouTube ↗</a></div>' +
    '</div>';
  }).join('') + '</div>';
}

function renderQuizzesPanel(quizzes: CourseMaterial[], canManage: boolean): string {
  if (!quizzes.length) return '<div class="attendance-empty"><h3>No quizzes yet</h3><p>Create quizzes on Kahoot and paste the shareable game-pin link here — students click straight into the live game.</p>' + (canManage ? '<button type="button" class="btn btn-primary" data-cd-action="add">+ Add first quiz</button>' : '') + '</div>';
  return '<div class="quiz-grid stagger">' + quizzes.map((q) => '<div class="card card-pad-sm quiz-card"><div class="row">' +
    '<div class="avatar av-c4" style="font-size:24px">🎯</div>' +
    '<div style="flex:1;min-width:0"><div class="t-strong">' + escapeHtml(q.title) + '</div><div class="t-meta t-mono" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + escapeHtml(q.url) + '</div>' +
    (q.note ? '<div class="t-meta">' + escapeHtml(q.note) + '</div>' : '') + '</div>' +
    '<a class="btn btn-accent btn-sm" href="' + escapeHtml(q.url) + '" target="_blank" rel="noopener">Open ↗</a>' +
    (canManage ? '<button type="button" class="btn btn-ghost btn-sm" data-cd-delete="' + q.id + '" title="Delete">✕</button>' : '') +
    '</div></div>').join('') + '</div>';
}

function renderNotesPanel(notes: CourseMaterial[], canManage: boolean): string {
  if (!notes.length) return '<div class="attendance-empty"><h3>No notes yet</h3><p>Paste raw text/markdown, link an external resource, or upload a small document (≤500 KB — PDF, DOCX, TXT, MD). For larger study material, store it in Drive/Notion and paste the share link.</p>' + (canManage ? '<button type="button" class="btn btn-primary" data-cd-action="add">+ Add first note</button>' : '') + '</div>';
  return '<div class="stack stagger">' + notes.map((n) => '<div class="card card-pad-sm note-card" data-cd-mat="' + n.id + '" style="cursor:pointer"><div class="row">' +
    '<div class="avatar av-c7">📄</div>' +
    '<div style="flex:1;min-width:0"><div class="t-strong">' + escapeHtml(n.title) + '</div>' +
    (n.attachmentName ? '<div class="t-meta">📎 ' + escapeHtml(n.attachmentName) + '</div>' : '') +
    '<div class="t-meta">' + escapeHtml(((n.note || n.url || '').slice(0, 200)) + ((n.note || n.url || '').length > 200 ? '…' : '')) + '</div></div>' +
    (canManage ? '<button type="button" class="btn btn-ghost btn-sm" data-cd-delete="' + n.id + '" title="Delete">✕</button>' : '') +
    '</div></div>').join('') + '</div>';
}

export function bindCourseDetail(): void {
  // NOTE: do NOT capture courseId here — handlers must read it lazily from the
  // current hash. The user navigates between courses and bind is only ever
  // called once (see main.ts boundOnce), so a closure snapshot would go stale.
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const tab = t.closest<HTMLElement>('[data-cd-tab]');
    if (tab && tab.dataset.cdTab) { activeTab = tab.dataset.cdTab as Tab; (window as any).__rerender?.(); return; }
    if (t.closest('[data-cd-action="add"]')) {
      const cid = courseIdFromHash();
      if (!cid) { toast('Open a course first', 'warn'); return; }
      openAddMaterial(cid, activeTab);
      return;
    }
    const card = t.closest<HTMLElement>('[data-cd-mat]');
    if (card && card.dataset.cdMat && !t.closest('[data-cd-delete]')) {
      const m = (materialsCache || []).find((x) => x.id === card.dataset.cdMat);
      if (m && (m.type === 'note' || m.type === 'document' || m.type === 'ppt')) {
        openNoteViewer(m);
      }
      return;
    }
    const del = t.closest<HTMLElement>('[data-cd-delete]');
    if (del && del.dataset.cdDelete) {
      const cid = courseIdFromHash();
      if (!cid) return;
      if (!window.confirm('Remove this material?')) return;
      void deleteCourseMaterial(cid, del.dataset.cdDelete).then(() => {
        materialsCache = null;
        cachedMaterialsCourseId = '';
        toast('Removed', 'success');
        (window as any).__rerender?.();
      }).catch((err) => toast(err instanceof Error ? err.message : 'Delete failed', 'warn'));
    }
  });

  document.addEventListener('submit', async (e) => {
    const form = (e.target as HTMLElement).closest<HTMLFormElement>('#cd-add-form');
    if (!form) return;
    e.preventDefault();
    const cid = courseIdFromHash();
    if (!cid) { toast('Course context lost — open a course first', 'warn'); return; }
    const fd = new FormData(form);
    const type = String(fd.get('_type') || 'video') as 'video' | 'quiz' | 'note';
    const title = String(fd.get('title') || '').trim();
    const url = String(fd.get('url') || '').trim();
    const note = String(fd.get('note') || '').trim();
    if (!title) { toast('Title is required', 'warn'); return; }
    if ((type === 'video' || type === 'quiz') && !url) { toast('URL is required for ' + type, 'warn'); return; }
    if (type === 'video' && url && !youtubeId(url)) { toast('That doesn\'t look like a YouTube link. We accept youtu.be/X, youtube.com/watch?v=X, /embed/X, /shorts/X.', 'warn'); return; }

    // Optional file attachment for notes only — convert small files to a data URL
    // and store it on the same document. Limited to ~500 KB so we don't bloat
    // Firestore; for larger files, link to Drive/Notion instead.
    let attachmentUrl = '';
    let attachmentName = '';
    const file = form.querySelector<HTMLInputElement>('input[type="file"]')?.files?.[0];
    if (type === 'note' && file) {
      if (file.size > 500 * 1024) { toast('Files over 500 KB are not supported — link to Drive/Notion for big material', 'warn'); return; }
      attachmentName = file.name;
      attachmentUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader(); r.onload = () => resolve(String(r.result || '')); r.onerror = () => reject(new Error('Could not read file'));
        r.readAsDataURL(file);
      }).catch((err) => { toast((err instanceof Error ? err.message : 'File read failed'), 'warn'); return ''; });
    }

    const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]'); if (btn) btn.disabled = true;
    // OPTIMISTIC: append a temp record so the user sees it BEFORE Firestore roundtrips.
    const tempId = 'tmp-' + Date.now();
    materialsCache = materialsCache || [];
    materialsCache.push({ id: tempId, tenantId: '', courseId: cid, type: type as any, title, url, note, attachmentUrl, attachmentName, order: materialsCache.length, published: true } as any);
    (window as any).__rerender?.();
    try {
      await createCourseMaterial(cid, { type, title, url, note, attachmentUrl, attachmentName });
      materialsCache = null; cachedMaterialsCourseId = '';
      toast('Added — video now playing in this course', 'success');
      closeModal('cd-add');
      (window as any).__rerender?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not add material', 'warn');
      if (btn) btn.disabled = false;
    }
  });
}

function openAddMaterial(courseId: string, type: Tab): void {
  const titleByType: Record<Tab, string> = { videos: 'Add video', quizzes: 'Add Kahoot quiz', notes: 'Add note' };
  const subtitleByType: Record<Tab, string> = {
    videos: 'Paste a YouTube URL — uploads to YouTube must happen on youtube.com (private), then paste the share link here. Storage and analytics are free.',
    quizzes: 'Create your game on kahoot.com, copy the shareable game URL, and paste it here.',
    notes: 'Add study material — paste raw text/markdown, link an external resource, or upload a small file (≤500 KB).',
  };
  const urlLabel = type === 'quiz' ? 'Kahoot URL' : type === 'video' ? 'YouTube' : 'Link (optional)';
  openModal({
    id: 'cd-add',
    title: titleByType[type] + ' · ' + courseId.slice(0, 6),
    subtitle: subtitleByType[type],
    width: 560,
    html: '<form id="cd-add-form"><div class="stack-tight">' +
      '<input type="hidden" name="_type" value="' + type + '"/>' +
      '<label class="t-eyebrow">Title<input required name="title" placeholder="' + (type === 'video' ? 'Lecture 1 — What is a market?' : type === 'quiz' ? 'Week 1 pre-class assessment' : 'Step-by-step guide to…') + '" class="form-input"/></label>' +
      (type !== 'note' ? '<label class="t-eyebrow">' + urlLabel + '<input required name="url" type="url" placeholder="' + (type === 'video' ? 'https://youtu.be/abc123' : 'https://create.kahoot.it/share/...') + '" class="form-input"/></label>' : '') +
      (type === 'note' ? '<label class="t-eyebrow">External link (optional)<input name="url" type="url" placeholder="https://drive.google.com/…" class="form-input"/></label>' : '') +
      (type === 'quiz' ? '<label class="t-eyebrow">Note (optional)<input name="note" placeholder="e.g. Pre-class assessment" class="form-input"/></label>' : '') +
      '<label class="t-eyebrow">' + (type === 'note' ? 'Note body (markdown supported)' : 'Note (optional)') + '<textarea name="note" rows="' + (type === 'note' ? 8 : 3) + '" placeholder="' + (type === 'note' ? '## Step 1\n\nRead this first…' : 'Optional description') + '" class="form-input" style="min-height:auto;resize:vertical"></textarea></label>' +
      (type === 'note' ? '<label class="t-eyebrow">Attach a file (≤500 KB — PDF / DOCX / TXT / MD)<input name="attachment" type="file" accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg" class="form-input" style="padding:8px"/></label>' : '') +
      '</div><div class="row" style="margin-top:24px;gap:10px;justify-content:flex-end">' +
      '<button type="button" class="btn btn-ghost" data-cd-close>Cancel</button>' +
      '<button type="submit" class="btn btn-primary">Save</button>' +
      '</div></form>',
  }, (panel) => {
    panel.querySelectorAll('[data-cd-close]').forEach((b) => b.addEventListener('click', () => closeModal('cd-add')));
  });
}

function openNoteViewer(n: CourseMaterial): void {
  const bodyHtml = (n.note || '').trim()
    ? '<div style="white-space:pre-wrap;line-height:1.6;font-family:\'Fraunces\',serif;font-size:15px">' + escapeHtml(n.note || '') + '</div>'
    : '<div class="t-meta">No note body.</div>';
  openModal({
    id: 'cd-note-view',
    title: n.title || 'Note',
    subtitle: '',
    width: 640,
    html: '<div class="stack-tight">' + bodyHtml +
      (n.url ? '<a class="btn btn-accent btn-sm" href="' + escapeHtml(n.url) + '" target="_blank" rel="noopener">Open external link ↗</a>' : '') +
      (n.attachmentUrl && n.attachmentName
        ? '<div class="card card-pad-sm" style="border-left:3px solid var(--accent)"><div class="row"><div style="flex:1"><div class="t-strong">📎 ' + escapeHtml(n.attachmentName) + '</div><div class="t-meta">Attached file (stored on this material record)</div></div>' +
          '<a class="btn btn-accent btn-sm" href="' + escapeHtml(n.attachmentUrl) + '" download="' + escapeHtml(n.attachmentName) + '">Download</a></div></div>'
        : '') +
      '<div class="row" style="justify-content:flex-end;margin-top:12px"><button type="button" class="btn btn-primary" data-cd-close>Close</button></div></div>',
  }, (panel) => {
    panel.querySelectorAll('[data-cd-close]').forEach((b) => b.addEventListener('click', () => closeModal('cd-note-view')));
  });
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}